import React, { useState, Fragment } from 'react';
import { Button } from '../components/Button';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { StarIcon, QuoteIcon, ArrowRightIcon, ChevronDownIcon } from 'lucide-react';
import { PhoneIcon, MessageSquareIcon, ClockIcon, CalendarIcon, HelpCircleIcon, BellIcon, CheckIcon, PhoneForwardedIcon, MessageCircleIcon, CalendarCheckIcon, ZapIcon, WorkflowIcon, DatabaseIcon, BrainCircuitIcon } from 'lucide-react';
function ServiceHero() {
  return <section className="w-full bg-[#0F0F0F] py-20 md:py-32" data-id="element-3041">
      <div className="max-w-7xl mx-auto px-8 md:px-12 text-center" data-id="element-3042">
        <h1 className="font-['Poppins'] font-bold text-5xl md:text-7xl lg:text-8xl text-[#F5F5F5] mb-8 leading-tight" data-id="element-3043">
          Australia's Leading
          <br data-id="element-3044" />
          <span className="text-[#F1B530]" data-id="element-3045">AI Answering Service</span>
        </h1>

        <p className="font-['Inter'] text-xl md:text-2xl text-[#A0A0A0] max-w-4xl mx-auto mb-12 leading-relaxed font-light" data-id="element-3046">
          Professional call answering, 24/7 availability, and seamless
          integration. Trusted by thousands of Australian businesses.
        </p>

        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12" data-id="element-3047">
          <Button size="lg" data-id="element-3048">Start Free 7-Day Trial</Button>
          <Button variant="secondary" size="lg" data-id="element-3049">
            1300 XXX XXX
          </Button>
        </div>

        <div className="flex items-center justify-center gap-2 text-[#F1B530]" data-id="element-3050">
          {[...Array(5)].map((_, i) => <StarIcon key={i} className="w-6 h-6 fill-current" data-id="element-3051" />)}
          <span className="font-['Inter'] text-[#A0A0A0] ml-2" data-id="element-3052">
            4.9/5 from 500+ reviews
          </span>
        </div>
      </div>
    </section>;
}
function ValuePropositionAnimated() {
  const {
    ref,
    isVisible
  } = useScrollAnimation({
    threshold: 0.1
  });
  const benefits = [{
    icon: ClockIcon,
    title: '24/7 Instant Call Answering',
    description: 'Never miss a call again with round-the-clock AI coverage that picks up instantly'
  }, {
    icon: MessageSquareIcon,
    title: 'Customised Company Greeting',
    description: 'Fully branded experience that sounds like your business, not a generic service'
  }, {
    icon: PhoneIcon,
    title: 'Accurate Capture',
    description: 'Precise name, number, and message recording with intelligent transcription'
  }, {
    icon: BellIcon,
    title: 'Real-Time Call Summaries',
    description: 'Instant notifications with complete call context delivered immediately'
  }, {
    icon: HelpCircleIcon,
    title: 'FAQ Handling',
    description: 'Answer common questions with fenced, brand-safe responses automatically'
  }, {
    icon: CalendarIcon,
    title: 'Appointment Booking',
    description: 'Seamless calendar integration for automated scheduling and confirmations'
  }];
  return <section className="w-full bg-[#1A1A1A] py-28 md:py-36" data-id="element-3053">
      <div ref={ref} className={`max-w-7xl mx-auto px-8 md:px-12 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} data-id="element-3054">
        <div className="text-center mb-20" data-id="element-3055">
          <h2 className="font-['Poppins'] font-bold text-4xl md:text-5xl lg:text-6xl text-[#F5F5F5] mb-6 tracking-tight" data-id="element-3056">
            Why Choose Our AI Answering Service?
          </h2>
          <p className="font-['Inter'] font-light text-xl md:text-2xl text-[#A0A0A0]" data-id="element-3057">
            Your Always-On Receptionist, without the overheads
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-3058">
          {benefits.map((benefit, index) => {
          const Icon = benefit.icon;
          return <div key={index} className="bg-[#252525] rounded-2xl p-10 border border-[#2A2A2A] hover:border-[#F1B530]/30 transition-all duration-300 hover:-translate-y-1" data-id="element-3059">
                <div className="w-14 h-14 bg-[#F1B530]/10 rounded-xl flex items-center justify-center mb-6 border border-[#F1B530]/20" data-id="element-3060">
                  <Icon className="w-7 h-7 text-[#F1B530]" data-id="element-3061" />
                </div>
                <h3 className="font-['Inter'] font-semibold text-xl text-[#F5F5F5] mb-4" data-id="element-3062">
                  {benefit.title}
                </h3>
                <p className="font-['Inter'] text-[#A0A0A0] leading-relaxed font-light" data-id="element-3063">
                  {benefit.description}
                </p>
              </div>;
        })}
        </div>
      </div>
    </section>;
}
function ProcessStepsAnimated() {
  const {
    ref,
    isVisible
  } = useScrollAnimation({
    threshold: 0.1
  });
  const steps = [{
    number: '01',
    title: 'Sign Up',
    description: 'Create your account and customize your AI receptionist in minutes'
  }, {
    number: '02',
    title: 'Configure',
    description: 'Set your greeting, FAQs, and routing rules to match your business'
  }, {
    number: '03',
    title: 'Go Live',
    description: 'Forward your calls and let AI handle everything 24/7'
  }, {
    number: '04',
    title: 'Grow',
    description: 'Monitor performance, capture leads, and scale effortlessly'
  }];
  return <section className="w-full bg-[#0F0F0F] py-28" data-id="element-3064">
      <div ref={ref} className={`max-w-7xl mx-auto px-8 md:px-12 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} data-id="element-3065">
        <div className="text-center mb-20" data-id="element-3066">
          <h2 className="font-['Poppins'] font-bold text-5xl md:text-6xl text-[#F5F5F5] mb-6" data-id="element-3067">
            How It Works
          </h2>
          <p className="font-['Inter'] text-xl text-[#A0A0A0] font-light" data-id="element-3068">
            Get started in 4 simple steps
          </p>
        </div>

        <div className="hidden lg:flex items-stretch gap-4" data-id="element-3069">
          {steps.map((step, index) => <Fragment key={index} data-id="element-3070">
              <div className="flex-1 bg-[#252525] border border-[#2A2A2A] rounded-2xl p-8 hover:border-[#F1B530]/30 transition-all duration-300 flex flex-col" data-id="element-3071">
                <div className="font-['Poppins'] text-6xl font-bold text-[#F1B530] mb-6 opacity-50" data-id="element-3072">
                  {step.number}
                </div>
                <h3 className="font-['Inter'] font-bold text-2xl text-[#F5F5F5] mb-4" data-id="element-3073">
                  {step.title}
                </h3>
                <p className="font-['Inter'] text-[#A0A0A0] leading-relaxed font-light" data-id="element-3074">
                  {step.description}
                </p>
              </div>

              {index < steps.length - 1 && <div className="flex items-center justify-center px-2" data-id="element-3075">
                  <ArrowRightIcon className="w-6 h-6 text-[#F1B530]" strokeWidth={2} data-id="element-3076" />
                </div>}
            </Fragment>)}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:hidden" data-id="element-3077">
          {steps.map((step, index) => <div key={index} className="bg-[#252525] border border-[#2A2A2A] rounded-2xl p-8 hover:border-[#F1B530]/30 transition-all duration-300 flex flex-col" data-id="element-3078">
              <div className="font-['Poppins'] text-6xl font-bold text-[#F1B530] mb-6 opacity-50" data-id="element-3079">
                {step.number}
              </div>
              <h3 className="font-['Inter'] font-bold text-2xl text-[#F5F5F5] mb-4" data-id="element-3080">
                {step.title}
              </h3>
              <p className="font-['Inter'] text-[#A0A0A0] leading-relaxed font-light" data-id="element-3081">
                {step.description}
              </p>
            </div>)}
        </div>
      </div>
    </section>;
}
function IndustryBenefits() {
  const {
    ref,
    isVisible
  } = useScrollAnimation({
    threshold: 0.1
  });
  const industries = [{
    name: 'Healthcare',
    benefits: ['HIPAA compliant', 'Appointment scheduling', 'Emergency routing', 'Patient messaging']
  }, {
    name: 'Legal',
    benefits: ['Client intake', 'Confidential handling', 'Urgent call screening', 'Case management']
  }, {
    name: 'Real Estate',
    benefits: ['Property inquiries', 'Viewing bookings', 'Lead capture', 'Tenant support']
  }, {
    name: 'Trade Services',
    benefits: ['Job booking', 'Emergency calls', 'Quote requests', 'Service scheduling']
  }, {
    name: 'Retail',
    benefits: ['Order taking', 'Store inquiries', 'Stock checks', 'Customer support']
  }, {
    name: 'Professional Services',
    benefits: ['Client screening', 'Appointment booking', 'Message handling', 'Consultation scheduling']
  }];
  return <section className="w-full bg-[#1A1A1A] py-28" data-id="element-3082">
      <div ref={ref} className={`max-w-7xl mx-auto px-8 md:px-12 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} data-id="element-3083">
        <div className="text-center mb-16" data-id="element-3084">
          <h2 className="font-['Poppins'] font-bold text-5xl md:text-6xl text-[#F5F5F5] mb-6" data-id="element-3085">
            Trusted Across Industries
          </h2>
          <p className="font-['Inter'] text-xl text-[#A0A0A0] font-light" data-id="element-3086">
            Tailored solutions for every business type
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-3087">
          {industries.map((industry, index) => <div key={index} className="bg-[#252525] border border-[#2A2A2A] rounded-2xl p-8 hover:border-[#F1B530]/30 transition-all duration-300" data-id="element-3088">
              <h3 className="font-['Poppins'] font-bold text-2xl text-[#F5F5F5] mb-6" data-id="element-3089">
                {industry.name}
              </h3>
              <ul className="space-y-3" data-id="element-3090">
                {industry.benefits.map((benefit, idx) => <li key={idx} className="font-['Inter'] text-[#A0A0A0] flex items-start" data-id="element-3091">
                    <span className="text-[#F1B530] mr-2" data-id="element-3092">•</span>
                    {benefit}
                  </li>)}
              </ul>
            </div>)}
        </div>
      </div>
    </section>;
}
function PricingPlansAnimated() {
  const {
    ref,
    isVisible
  } = useScrollAnimation({
    threshold: 0.1
  });
  const plans = [{
    name: 'Scout 25',
    price: 'AUD $65',
    calls: '25 calls/month',
    minutes: '125',
    target: 'Perfect for solo operators testing the waters',
    inclusions: ['Instant call pickup', 'Customised company greeting', 'Name, number & message capture', 'Real-time call summaries via SMS/email', 'Call recordings & transcripts', 'Dashboard analytics', 'Excess: $2.60/call or $0.52/min'],
    ctaText: 'Start 7 Day Free Trial',
    ctaLink: 'Listen to call recording'
  }, {
    name: 'Forager 50',
    price: 'AUD $115',
    calls: '50 calls/month',
    minutes: '250',
    target: 'Small teams with steady enquiry flow',
    inclusions: ['Everything in Scout 25', 'Priority support', 'Extended call handling', 'Multi-user dashboard access', 'Custom call routing rules', 'Excess: $2.30/call or $0.46/min'],
    ctaText: 'Try 7 Day Free Trial',
    ctaLink: 'Listen to call recording',
    isPopular: true
  }, {
    name: 'Drift 75',
    price: 'AUD $165',
    calls: '75 calls/month',
    minutes: '375',
    target: 'Growing teams with rising call peaks',
    inclusions: ['Everything in Forager 50', 'Advanced analytics', 'Team collaboration tools', 'Custom integrations', 'Dedicated account manager', 'Excess: $2.20/call or $0.44/min'],
    ctaText: 'Try 7 Day Free Trial',
    ctaLink: 'Listen to call recording'
  }, {
    name: 'Surge 150',
    price: 'AUD $325',
    calls: '150 calls/month',
    minutes: '750',
    target: 'High-tempo operations with regular spikes',
    inclusions: ['Everything in Drift 75', 'Priority call routing', 'Advanced workflow automation', 'Custom reporting', 'API access', 'Excess: $2.17/call or $0.43/min'],
    ctaText: 'Try 7 Day Free Trial',
    ctaLink: 'Listen to call recording'
  }, {
    name: 'Swarm 300',
    price: 'AUD $645',
    calls: '300 calls/month',
    minutes: '1,500',
    target: 'Heavy daily call activity teams',
    inclusions: ['Everything in Surge 150', 'Enterprise-grade security', 'Custom AI training', 'White-label options', 'SLA guarantees', 'Excess: $2.15/call or $0.43/min'],
    ctaText: 'Try 7 Day Free Trial',
    ctaLink: 'Listen to call recording'
  }, {
    name: 'Colony 500',
    price: 'AUD $955',
    calls: '500 calls/month',
    minutes: '2,250',
    target: 'Enterprise-level high-volume operations',
    inclusions: ['Everything in Swarm 300', 'Dedicated infrastructure', 'Custom AI models', 'Advanced compliance features', '24/7 premium support', 'Excess: $1.91/call or $0.42/min'],
    ctaText: 'Try 7 Day Free Trial',
    ctaLink: 'Listen to call recording'
  }, {
    name: 'AI Automation Customisation',
    target: 'For businesses requiring advanced automation and integrations beyond standard plans',
    inclusions: ['Custom call volumes', 'Bespoke AI training', 'Enterprise integrations', 'Advanced workflow automation', 'Dedicated success team', 'Custom SLAs'],
    ctaText: 'Contact Sales',
    isCustom: true
  }];
  return <section className="w-full bg-[#0F0F0F] py-28 md:py-36" data-id="element-3093">
      <div ref={ref} className={`max-w-7xl mx-auto px-8 md:px-12 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} data-id="element-3094">
        <h2 className="font-['Poppins'] font-bold text-5xl md:text-6xl text-[#F5F5F5] text-center mb-20 tracking-tight" data-id="element-3095">
          Plans
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12" data-id="element-3096">
          {plans.slice(0, 6).map((plan, index) => <div key={index} className={`bg-[#252525] rounded-2xl p-10 border transition-all duration-300 hover:-translate-y-1 relative flex flex-col h-full ${plan.isPopular ? 'border-[#F1B530] ring-2 ring-[#F1B530]/20' : 'border-[#2A2A2A] hover:border-[#F1B530]/30'}`} data-id="element-3097">
              {plan.isPopular && <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-[#F1B530] text-[#0F0F0F] px-6 py-2 rounded-full font-['Inter'] font-semibold text-sm" data-id="element-3098">
                  Most Popular
                </div>}

              <div className="mb-8" data-id="element-3099">
                <h3 className="font-['Poppins'] font-bold text-2xl text-[#F5F5F5] mb-3" data-id="element-3100">
                  {plan.name}
                </h3>
                <div className="mb-3" data-id="element-3101">
                  <span className="font-['Poppins'] font-bold text-5xl text-[#F5F5F5]" data-id="element-3102">
                    {plan.price}
                  </span>
                  <span className="text-[#A0A0A0] font-['Inter'] ml-2 text-sm" data-id="element-3103">
                    plus GST/month
                  </span>
                </div>
                <p className="text-[#A0A0A0] font-['Inter'] text-sm mb-2 font-light" data-id="element-3104">
                  Up to {plan.calls} • {plan.minutes} free minutes
                </p>
                <p className="text-[#A0A0A0] font-['Inter'] text-sm italic font-light" data-id="element-3105">
                  {plan.target}
                </p>
              </div>

              <div className="mb-10 flex-grow" data-id="element-3106">
                <ul className="space-y-4" data-id="element-3107">
                  {plan.inclusions.map((inclusion, idx) => <li key={idx} className="flex items-start" data-id="element-3108">
                      <CheckIcon className="w-5 h-5 text-[#F1B530] mr-3 flex-shrink-0 mt-0.5" data-id="element-3109" />
                      <span className="font-['Inter'] text-[#F5F5F5] text-sm leading-relaxed font-light" data-id="element-3110">
                        {inclusion}
                      </span>
                    </li>)}
                </ul>
              </div>

              <div className="mt-auto" data-id="element-3111">
                <Button className="w-full" size="lg" data-id="element-3112">
                  {plan.ctaText}
                </Button>

                {plan.ctaLink && <a href="#" className="block text-center mt-5 text-[#A0A0A0] font-['Inter'] text-sm hover:text-[#F1B530] transition-colors duration-300" data-id="element-3113">
                    {plan.ctaLink}
                  </a>}
              </div>
            </div>)}
        </div>

        <div className="max-w-2xl mx-auto" data-id="element-3114">
          <div className="bg-[#252525] rounded-2xl p-10 border border-[#2A2A2A] hover:border-[#F1B530]/30 transition-all duration-300 hover:-translate-y-1 flex flex-col h-full" data-id="element-3115">
            <div className="mb-8" data-id="element-3116">
              <h3 className="font-['Poppins'] font-bold text-2xl text-[#F5F5F5] mb-3" data-id="element-3117">
                {plans[6].name}
              </h3>
              <div className="mb-3" data-id="element-3118">
                <span className="font-['Poppins'] font-bold text-4xl text-[#F5F5F5]" data-id="element-3119">
                  Custom Pricing
                </span>
              </div>
              <p className="text-[#A0A0A0] font-['Inter'] text-sm italic font-light" data-id="element-3120">
                {plans[6].target}
              </p>
            </div>

            <div className="mb-10 flex-grow" data-id="element-3121">
              <ul className="space-y-4" data-id="element-3122">
                {plans[6].inclusions.map((inclusion, idx) => <li key={idx} className="flex items-start" data-id="element-3123">
                    <CheckIcon className="w-5 h-5 text-[#F1B530] mr-3 flex-shrink-0 mt-0.5" data-id="element-3124" />
                    <span className="font-['Inter'] text-[#F5F5F5] text-sm leading-relaxed font-light" data-id="element-3125">
                      {inclusion}
                    </span>
                  </li>)}
              </ul>
            </div>

            <div className="mt-auto" data-id="element-3126">
              <Button className="w-full" size="lg" data-id="element-3127">
                {plans[6].ctaText}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}
function Testimonials() {
  const {
    ref,
    isVisible
  } = useScrollAnimation({
    threshold: 0.1
  });
  const testimonials = [{
    quote: "Since switching to this AI answering service, we haven't missed a single lead. The 24/7 coverage is a game-changer for our business.",
    author: 'Sarah Mitchell',
    role: 'Director, Mitchell & Associates Law',
    rating: 5
  }, {
    quote: 'The appointment booking feature alone has saved us 10+ hours per week. Our clients love the instant response time.',
    author: 'James Chen',
    role: 'Practice Manager, Sydney Dental Care',
    rating: 5
  }, {
    quote: 'Professional, reliable, and cost-effective. This service pays for itself in captured opportunities. Highly recommended.',
    author: 'Emma Thompson',
    role: 'Owner, Thompson Real Estate',
    rating: 5
  }];
  return <section className="w-full bg-[#0F0F0F] py-28" data-id="element-3128">
      <div ref={ref} className={`max-w-7xl mx-auto px-8 md:px-12 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} data-id="element-3129">
        <div className="text-center mb-16" data-id="element-3130">
          <h2 className="font-['Poppins'] font-bold text-5xl md:text-6xl text-[#F5F5F5] mb-6" data-id="element-3131">
            What Our Clients Say
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-3132">
          {testimonials.map((testimonial, index) => <div key={index} className="bg-[#252525] border border-[#2A2A2A] rounded-2xl p-8 relative" data-id="element-3133">
              <QuoteIcon className="w-12 h-12 text-[#F1B530]/20 mb-6" data-id="element-3134" />

              <div className="flex gap-1 mb-6" data-id="element-3135">
                {[...Array(testimonial.rating)].map((_, i) => <StarIcon key={i} className="w-5 h-5 text-[#F1B530] fill-current" data-id="element-3136" />)}
              </div>

              <p className="font-['Inter'] text-[#F5F5F5] mb-6 leading-relaxed font-light" data-id="element-3137">
                "{testimonial.quote}"
              </p>

              <div data-id="element-3138">
                <div className="font-['Inter'] font-semibold text-[#F5F5F5]" data-id="element-3139">
                  {testimonial.author}
                </div>
                <div className="font-['Inter'] text-sm text-[#A0A0A0]" data-id="element-3140">
                  {testimonial.role}
                </div>
              </div>
            </div>)}
        </div>
      </div>
    </section>;
}
function AddOnsAnimated() {
  const {
    ref,
    isVisible
  } = useScrollAnimation({
    threshold: 0.1
  });
  const addOns = [{
    icon: PhoneForwardedIcon,
    name: 'Live Transfer',
    price: 'AUD $99',
    setup: 'one-time setup',
    features: ['Instant Escalation for Time Sensitive Opportunities', 'Transfers With Full Caller Context', 'Smart Routing Based on Your Real Business Rules']
  }, {
    icon: MessageCircleIcon,
    name: 'FAQ Handling',
    price: 'AUD $299',
    setup: 'one-time fee',
    features: ['Turn AI agent into trained front-line knowledge assistant', 'Fenced responses using only approved information', 'Clear brand-safe responses', 'Faster resolution & reduced message volume']
  }, {
    icon: CalendarCheckIcon,
    name: 'Appointment Booking',
    price: 'AUD $399',
    setup: 'one-time setup',
    features: ['Real-time calendar syncing (Google, Outlook, etc.)', 'Automated booking and confirmation', 'Custom booking rules', 'Caller notifications and internal alerts']
  }];
  return <section className="w-full bg-[#1A1A1A] py-28 md:py-36" data-id="element-3141">
      <div ref={ref} className={`max-w-7xl mx-auto px-8 md:px-12 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} data-id="element-3142">
        <div className="text-center mb-20" data-id="element-3143">
          <h2 className="font-['Poppins'] font-bold text-4xl md:text-5xl text-[#F5F5F5] mb-6 tracking-tight" data-id="element-3144">
            Universal Add-Ons
          </h2>
          <p className="font-['Inter'] text-lg text-[#A0A0A0] max-w-3xl mx-auto font-light" data-id="element-3145">
            Available for all plans to extend your AI answering service
            capabilities
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-3146">
          {addOns.map((addOn, index) => {
          const Icon = addOn.icon;
          return <div key={index} className="bg-[#252525] rounded-2xl overflow-hidden border border-[#2A2A2A] hover:border-[#F1B530]/50 transition-all duration-300 hover:-translate-y-1" data-id="element-3147">
                <div className="p-10" data-id="element-3148">
                  <div className="w-14 h-14 bg-[#F1B530] rounded-xl flex items-center justify-center mb-6" data-id="element-3149">
                    <Icon className="w-7 h-7 text-[#0F0F0F]" data-id="element-3150" />
                  </div>

                  <h3 className="font-['Poppins'] font-bold text-2xl text-[#F5F5F5] mb-3" data-id="element-3151">
                    {addOn.name}
                  </h3>

                  <div className="mb-8" data-id="element-3152">
                    <span className="font-['Poppins'] font-semibold text-2xl text-[#F1B530]" data-id="element-3153">
                      {addOn.price}
                    </span>
                    <span className="text-[#A0A0A0] font-['Inter'] text-sm ml-2" data-id="element-3154">
                      {addOn.setup}
                    </span>
                  </div>

                  <ul className="space-y-4" data-id="element-3155">
                    {addOn.features.map((feature, idx) => <li key={idx} className="flex items-start" data-id="element-3156">
                        <span className="text-[#F1B530] mr-3 flex-shrink-0 text-lg" data-id="element-3157">
                          •
                        </span>
                        <span className="font-['Inter'] text-[#F5F5F5] text-sm leading-relaxed font-light" data-id="element-3158">
                          {feature}
                        </span>
                      </li>)}
                  </ul>
                </div>
              </div>;
        })}
        </div>
      </div>
    </section>;
}
function AdvancedAutomationAnimated() {
  const {
    ref,
    isVisible
  } = useScrollAnimation({
    threshold: 0.1
  });
  const capabilities = [{
    icon: WorkflowIcon,
    text: 'Route leads intelligently'
  }, {
    icon: ZapIcon,
    text: 'Trigger automated workflows'
  }, {
    icon: DatabaseIcon,
    text: 'Sync directly into CRMs'
  }, {
    icon: BrainCircuitIcon,
    text: 'Collect structured information'
  }];
  return <section className="w-full bg-[#0F0F0F] py-28 md:py-36" data-id="element-3159">
      <div ref={ref} className={`max-w-6xl mx-auto px-8 md:px-12 text-center transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} data-id="element-3160">
        <h2 className="font-['Poppins'] font-bold text-4xl md:text-5xl lg:text-6xl mb-6 text-[#F5F5F5] tracking-tight" data-id="element-3161">
          AI Automation Customisation Advanced
        </h2>
        <p className="font-['Inter'] font-light text-xl md:text-2xl text-[#A0A0A0] mb-10" data-id="element-3162">
          Automate More Than Calls. Automate Your Entire Frontline.
        </p>

        <p className="font-['Inter'] text-lg text-[#A0A0A0] max-w-4xl mx-auto mb-16 leading-relaxed font-light" data-id="element-3163">
          For businesses handling 500+ calls per month, unlock advanced
          capabilities that transform your AI answering service into a complete
          frontline automation platform.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16" data-id="element-3164">
          {capabilities.map((capability, index) => {
          const Icon = capability.icon;
          return <div key={index} className="bg-[#252525] rounded-2xl p-8 border border-[#2A2A2A] hover:border-[#F1B530]/30 transition-all duration-300 h-full flex flex-col items-center justify-center min-h-[180px]" data-id="element-3165">
                <Icon className="w-10 h-10 text-[#F1B530] mx-auto mb-5" data-id="element-3166" />
                <p className="font-['Inter'] text-[#F5F5F5] font-light text-center" data-id="element-3167">
                  {capability.text}
                </p>
              </div>;
        })}
        </div>

        <Button size="lg" data-id="element-3168">Contact Sales</Button>
      </div>
    </section>;
}
function FAQAnimated() {
  const {
    ref,
    isVisible
  } = useScrollAnimation({
    threshold: 0.1
  });
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const faqs = [{
    question: 'How does the AI answering service work?',
    answer: 'Our AI receptionist answers calls 24/7, captures caller information, handles FAQs, and can even book appointments. It integrates seamlessly with your existing systems and can be customized to match your business needs.'
  }, {
    question: 'Can I customize the greeting and responses?',
    answer: 'Absolutely! You can fully customize your company greeting, upload FAQs, set routing rules, and define your brand voice. The AI learns from your business to provide accurate, on-brand responses.'
  }, {
    question: "What happens if the AI can't handle a call?",
    answer: 'The AI can transfer calls to your team with full context when needed. You set the rules for when calls should be escalated, ensuring important matters always reach the right person.'
  }, {
    question: 'How quickly can I get started?',
    answer: "Setup takes less than 5 minutes. Simply sign up, customize your greeting and settings, forward your number, and you're live. We provide step-by-step guidance throughout the process."
  }, {
    question: 'Is there a contract or commitment?',
    answer: 'No long-term contracts required. Start with our 7-day free trial, then choose a monthly plan that fits your needs. You can upgrade, downgrade, or cancel anytime.'
  }];
  return <section className="w-full bg-[#1A1A1A] py-28 md:py-36" data-id="element-3169">
      <div ref={ref} className={`max-w-4xl mx-auto px-8 md:px-12 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} data-id="element-3170">
        <div className="text-center mb-16" data-id="element-3171">
          <h2 className="font-['Poppins'] font-bold text-4xl md:text-5xl text-[#F5F5F5] mb-6" data-id="element-3172">
            Frequently Asked Questions
          </h2>
          <p className="font-['Inter'] text-lg text-[#A0A0A0] font-light" data-id="element-3173">
            Everything you need to know about our AI answering service
          </p>
        </div>

        <div className="space-y-4" data-id="element-3174">
          {faqs.map((faq, index) => <div key={index} className="bg-[#252525] border border-[#2A2A2A] rounded-2xl overflow-hidden transition-all duration-300 hover:border-[#F1B530]/30" data-id="element-3175">
              <button onClick={() => setOpenIndex(openIndex === index ? null : index)} className="w-full px-8 py-6 flex items-center justify-between text-left" data-id="element-3176">
                <span className="font-['Inter'] font-semibold text-xl text-[#F5F5F5] pr-8" data-id="element-3177">
                  {faq.question}
                </span>
                <ChevronDownIcon className={`w-6 h-6 text-[#F1B530] flex-shrink-0 transition-transform duration-300 ${openIndex === index ? 'rotate-180' : ''}`} data-id="element-3178" />
              </button>

              <div className={`overflow-hidden transition-all duration-300 ${openIndex === index ? 'max-h-96' : 'max-h-0'}`} data-id="element-3179">
                <div className="px-8 pb-6" data-id="element-3180">
                  <p className="font-['Inter'] text-[#A0A0A0] leading-relaxed font-light" data-id="element-3181">
                    {faq.answer}
                  </p>
                </div>
              </div>
            </div>)}
        </div>
      </div>
    </section>;
}
export function PhoneServicesInspired() {
  return <div className="w-full min-h-screen bg-[#0F0F0F]" data-id="element-3182">
      <ServiceHero data-id="element-3183" />
      <ValuePropositionAnimated data-id="element-3184" />
      <ProcessStepsAnimated data-id="element-3185" />
      <IndustryBenefits data-id="element-3186" />
      <PricingPlansAnimated data-id="element-3187" />
      <Testimonials data-id="element-3188" />
      <AddOnsAnimated data-id="element-3189" />
      <AdvancedAutomationAnimated data-id="element-3190" />
      <FAQAnimated data-id="element-3191" />
    </div>;
}